import React, { useCallback, useState, useMemo } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import isString from 'lodash/isString';
import { useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';

// FUNCTIONS
import { sleep } from 'functions.js';
import { sidebarActions } from 'actions.js';

// GLOBAL COMPONENTS
import Button from 'core/tools/Button';
import Form from 'core/form/Form';
import Sidebar from 'parts/sidebar/Sidebar';

// STYLES
import styles from './confirmCancel.module.scss';

// MAIN COMPONENT
const ConfirmCancel = ({ onClose, cancelledIndex: index }) => {
  const [cancelComment, setCancelComment] = useState();

  const { values, setFieldValue, setFieldError } = useFormikContext() || {};
  const dispatch = useDispatch();

  const handleClose = useCallback(() => {
    dispatch(sidebarActions.close());
    onClose();
    setCancelComment();
  }, [dispatch, onClose]);

  const { item_name, lock_cost } = useMemo(
    () => values?.aryDealerAMFinance?.[index] || {},
    [index, values.aryDealerAMFinance]
  );

  const handleConfirmCancel = useCallback(async () => {
    setCancelComment(cancelComment || '');
    if (!cancelComment) return;

    setFieldValue(`aryDealerAMFinance[${index}].is_checked`, false);
    setFieldValue(`aryDealerAMFinance[${index}].isUpdated`, false);
    setFieldValue(`aryDealerAMFinance[${index}].isDeleted`, true);
    // Clear product values
    const item_selling_price = `aryDealerAMFinance[${index}].item_selling_price`;
    const item_cost = `aryDealerAMFinance[${index}].item_cost`;

    // Clear any errors
    setFieldError(item_selling_price, undefined);
    if (!lock_cost) setFieldError(item_cost, undefined);

    await sleep(200);

    // Clear product values
    setFieldValue(`aryDealerAMFinance[${index}].item_gross`, '');
    setFieldValue(item_selling_price, '');
    if (!lock_cost) setFieldValue(item_cost, '');

    setFieldValue(`aryDealerAMFinance[${index}].cancel_comment`, cancelComment);

    handleClose();
  }, [cancelComment, setFieldValue, index, setFieldError, lock_cost, handleClose]);

  return (
    <Sidebar
      name="amFinanceConfirmCancelSidebar"
      title="Confirm Cancel"
      className={styles.sidebar}
      closeButton={{ onClick: handleClose }}
    >
      <div className={clsx('mt-4 mb-5 large', styles.text)}>
        <p>Are you sure you want to remove {item_name}?</p>
        <p>Please provide the reason for this product cancellation:</p>
        <Form.Textarea
          placeholder="Please enter your comment here"
          formGroup={{
            className: 'px-0'
          }}
          useFormik={false}
          value={cancelComment}
          onChange={({ target: { value } }) => setCancelComment(value)}
          vertical
          fullWidth
          error={isString(cancelComment) && !cancelComment.trim() ? 'Required Field.' : undefined}
        />
      </div>
      <Form.Row className="flex-grow-0">
        <Form.Col>
          <Button.Cancel
            icon={{
              use: 'arrow-left',
              order: 1
            }}
            justify="between"
            onClick={handleClose}
          />
        </Form.Col>
        <Form.Col>
          <Button label="Confirm" icon="checkmark" onClick={handleConfirmCancel} />
        </Form.Col>
      </Form.Row>
    </Sidebar>
  );
};

// EXPORT
export default ConfirmCancel;
